import { Icon, useWindowSize } from '@data-products-and-ai/react-components';

import { useMemo } from 'react';
import { MOBILE_WIDTH } from '@/constants';

import '@/components/Pagination/Pagination.scss';

interface PaginationProps {
  disabled?: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxVisiblePages = 5,
  disabled = false,
}) => {
  const { width } = useWindowSize();
  const isMobileSize = width <= MOBILE_WIDTH;

  const getVisiblePages = (): number[] => {
    const pages: number[] = [];

    if (isMobileSize) {
      return pages;
    }

    const middleCount = maxVisiblePages - 2;
    let start = Math.max(2, currentPage - Math.floor(middleCount / 2));
    let end = start + middleCount - 1;

    if (end >= totalPages) {
      end = totalPages - 1;
      start = Math.max(2, end - middleCount + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const visiblePages = useMemo(getVisiblePages, [
    currentPage,
    totalPages,
    maxVisiblePages,
    isMobileSize,
  ]);

  const handleClick = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  if (totalPages <= 1) return null;

  return (
    <nav>
      <ul className="pagination">
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => handleClick(currentPage - 1)}
            disabled={currentPage === 1 || disabled}
          >
            <Icon
              icon="IconCaretLeft"
              color={
                currentPage === 1 || disabled
                  ? 'var(--color-gray-light)'
                  : 'var(--color-primary)'
              }
            />
          </button>
        </li>

        <li className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
          <button
            className="page-link"
            disabled={disabled}
            onClick={() => handleClick(1)}
          >
            1
          </button>
        </li>

        {maxVisiblePages <= 3 ? (
          <>
            {visiblePages[0] > 2 && (
              <li className="page-item disabled">
                <span className="page-link">
                  <Icon
                    icon="IconOptionsHorizontal"
                    color="var(--color-black)"
                  />
                </span>
              </li>
            )}
            {visiblePages.map((page) => (
              <li
                key={page}
                className={`page-item ${page === currentPage ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  disabled={disabled}
                  onClick={() => handleClick(page)}
                >
                  {page}
                </button>
              </li>
            ))}
            {visiblePages[0] <= 2 &&
              visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                <li className="page-item disabled">
                  <span className="page-link">
                    <Icon
                      icon="IconOptionsHorizontal"
                      color="var(--color-black)"
                    />
                  </span>
                </li>
              )}
          </>
        ) : (
          <>
            {visiblePages[0] > 2 && (
              <li className="page-item disabled">
                <span className="page-link">
                  <Icon
                    icon="IconOptionsHorizontal"
                    color="var(--color-black)"
                  />
                </span>
              </li>
            )}

            {visiblePages.map((page) => (
              <li
                key={page}
                className={`page-item ${page === currentPage ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  disabled={disabled}
                  onClick={() => handleClick(page)}
                >
                  {page}
                </button>
              </li>
            ))}

            {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
              <li className="page-item disabled">
                <span className="page-link">
                  <Icon
                    icon="IconOptionsHorizontal"
                    color="var(--color-black)"
                  />
                </span>
              </li>
            )}
          </>
        )}

        <li
          className={`page-item ${currentPage === totalPages ? 'active' : ''}`}
        >
          <button
            className="page-link"
            disabled={disabled}
            onClick={() => handleClick(totalPages)}
          >
            {totalPages}
          </button>
        </li>

        <li
          className={`page-item ${
            currentPage === totalPages ? 'disabled' : ''
          }`}
        >
          <button
            className="page-link"
            onClick={() => handleClick(currentPage + 1)}
            disabled={currentPage === totalPages || disabled}
          >
            <Icon
              icon="IconCaretRight"
              color={
                currentPage === totalPages || disabled
                  ? 'var(--color-gray-light)'
                  : 'var(--color-primary)'
              }
            />
          </button>
        </li>
      </ul>
    </nav>
  );
};
